import { AuthConstants, ForgotPasswordConstants } from './types';
import { ProfileConstants, PasswordConstants } from '../user/types';
import { config } from '../../config';
import { BuildingActions } from '../building/actions';
import { NetworkService } from '../../services';

/* eslint-disable camelcase, quote-props */
const { BASE_URL } = config;

// API endpoints
const LOGIN_ENDPOINT = `${BASE_URL}/auth/api/user/login/`;
const VERIFY_TOKEN = `${BASE_URL}/auth/api/user/verify-token/`;
const REFRESH_TOKEN = `${BASE_URL}/auth/api/user/refresh-token/`;
const RESET_PASSWORD = `${BASE_URL}/auth/api/user/password-reset/`;
const RESET_PASSWORD_CONFIRM = `${BASE_URL}/auth/api/user/password-reset-confirm/`;
const VALIDATE_VERSION_ENDPOINT = `${BASE_URL}/auth/api/versions/validate/`;
const CREATE_VERSION_ENDPOINT = `${BASE_URL}/auth/api/versions/`;

export class AuthActions {
  static login(email, password) {
    return async (dispatch) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      };
      const response = await fetch(LOGIN_ENDPOINT, requestOptions);
      if (!response.ok) {
        return dispatch({ type: AuthConstants.LoginFailure, payload: response.statusText });
      }
      const user = await response.json();
      // login successful if there's a jwt token in the response
      if (user && user.token && user.refresh_token) {
        // store user details and jwt token in local storage to keep user logged
        await dispatch({ type: AuthConstants.LoginSuccess, payload: user });
        await dispatch({ type: ProfileConstants.SetProfile, payload: user.user });
        return dispatch(BuildingActions.getBuildingList());
      }
      return dispatch({ type: AuthConstants.LoginFailure, payload: 'Could not Authenticate' });
    };
  }

  static logout() {
    // remove tokens from local storage
    return { type: AuthConstants.Logout };
  }

  static validateToken() {
    return async (dispatch) => {
      // look for auth token & refresh token
      const token = JSON.parse(window.sessionStorage.getItem('token'));
      if (!token) {
        // throw error to call refresh token
        throw new Error('Validation problem');
      }
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      };

      const response = await fetch(VERIFY_TOKEN, requestOptions);

      if (!response.ok) {
        // throw error to call refresh token
        throw new Error('Validation problem');
      }

      const user = await response.json();

      // get reducer to automatically log user in
      await dispatch({ type: AuthConstants.LoginSuccess, payload: user });
      await dispatch({ type: ProfileConstants.SetProfile, payload: user.user });
      return dispatch(BuildingActions.getBuildingList());
    };
  }

  static refreshToken(url) {
    return async (dispatch) => {
      const refreshToken = JSON.parse(localStorage.getItem('refresh_token'));

      if (!refreshToken) {
        return dispatch({ type: AuthConstants.Logout });
      }

      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: refreshToken }),
      };

      const response = await fetch(REFRESH_TOKEN, requestOptions);
      if (!response.ok) {
        return dispatch({ type: AuthConstants.Logout });
      }

      const user = await response.json();
      // set a new token and log user in
      await dispatch({ type: AuthConstants.LoginSuccess, payload: user });
      await dispatch({ type: ProfileConstants.SetProfile, payload: user.user });
      return dispatch(BuildingActions.getBuildingList());
    };
  }

  static setLoginError(error) {
    return { type: AuthConstants.LoginError, payload: error };
  }

  static forgotPassword(email) {
    return async (dispatch) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      };

      const response = await fetch(RESET_PASSWORD, requestOptions);
      if (!response.ok) {
        const data = await response.json();
        const { message } = data.errors[0];
        return dispatch(AuthActions.setForgotPasswordError(message));
      }
      return dispatch({ type: ForgotPasswordConstants.EmailSent });
    };
  }

  static setForgotPasswordError(message) {
    return { type: ForgotPasswordConstants.EmailNotSent, payload: message };
  }

  static clearForgotPasswordMessages() {
    return { type: ForgotPasswordConstants.ClearStatus };
  }

  static resetPassword(newPassword1, newPassword2, user_hash, token) {
    return async (dispatch) => {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_password1: newPassword1,
          new_password2: newPassword2,
          user_hash: user_hash,
          token: token,
        }),
      };

      const response = await fetch(RESET_PASSWORD_CONFIRM, requestOptions);
      if (!response.ok) {
        const data = await response.json();
        const { message } = data.errors[0];
        return dispatch(AuthActions.setResetPasswordError(message));
      }
      const message = 'Your password has been updated. Log in with your new password.';

      return dispatch({ type: PasswordConstants.PasswordChanged, payload: message });
    };
  }

  static setResetPasswordError(message) {
    return { type: PasswordConstants.PasswordFailure, payload: message };
  }

  static clearResetPasswordError() {
    return { type: PasswordConstants.ClearStatus };
  }

  static validateVersion(version, id) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        const API_ENDPOINT = `${VALIDATE_VERSION_ENDPOINT}${id}?version=${version}`;
        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return response.json().message;
      }
      const data = await response.json();

      return data;
    };
  }

  static createVersion(is_latest, app, version) {
    return async (dispatch) => {
      const callback = () => {
        const body = JSON.stringify({
          is_latest,
          app,
          version,
        });

        const requestOptions = NetworkService.getRequestOptions('POST', body);
        return fetch(CREATE_VERSION_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return null;
      }

      return true;
    };
  }
}
